/*
 * Every date the app renders, in one place.
 *
 * There were five call sites with four different option objects, two locale
 * arguments and two fallbacks for an unparseable value (`''` and `'—'`). The
 * duplicated part was never the option objects — those are genuinely different
 * presentations — it was the parse-and-guard around them, written out four
 * times, and forgotten once.
 *
 * Collecting them here also makes one disagreement visible that was invisible
 * while they were scattered: see `formatTransactionDate`.
 */

/**
 * Parses and formats, or returns `fallback`.
 *
 * The guard matters more than it looks: `new Date('not-a-date')` is an Invalid
 * Date, and formatting one yields the literal string "Invalid Date" in the UI
 * rather than throwing. ContentPage still has no guard at all, which is why its
 * created-at label is routed through here now.
 */
function format(
  iso: string | null | undefined,
  options: Intl.DateTimeFormatOptions,
  fallback: string,
  locale?: string,
): string {
  if (!iso) return fallback;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString(locale, options);
}

/** `July 19` — the library card's meta line. */
export function formatDayMonth(iso: string | null | undefined, fallback = ''): string {
  return format(iso, { month: 'long', day: 'numeric' }, fallback);
}

/** `Jul 22, 2026` — activity rows and the developer-token table. */
export function formatShortDate(iso: string | null | undefined, fallback = ''): string {
  return format(iso, { month: 'short', day: 'numeric', year: 'numeric' }, fallback);
}

/**
 * `Jul 22, 2026`, forced to US formatting.
 *
 * Kept separate from `formatShortDate` rather than merged into it, because the
 * difference is not obviously intentional: the wallet's activity table is the
 * only surface in the app that pins a locale, so a user in London sees their
 * library card dates in their own format and their transaction dates in
 * America's. It reads like an oversight, but changing it is a product decision
 * about the wallet, not part of moving files around — so it is preserved
 * exactly and flagged here.
 */
export function formatTransactionDate(iso: string): string {
  return format(iso, { month: 'short', day: 'numeric', year: 'numeric' }, '', 'en-US');
}

/** `Monday, July 22, 2026 at 3:04 PM` — the content page's created-at line. */
export function formatLongDateTime(iso: string | null | undefined, fallback = ''): string {
  return format(
    iso,
    {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    },
    fallback,
  );
}
